import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { css } from "@emotion/core";
import useTranslation from "next-translate/useTranslation";
import { useTheme } from "emotion-theming";
import LocalizedLayout from "@components/LocalizedLayout";

import api, { cacheAsyncFn } from "api/api";

import { Theme } from "../../src/utils/styled";
import { withI18n, importNamespaces, getI18nPaths } from "../../src/utils/i18n";
import { GetStaticProps, GetStaticPaths } from "next";
import { stripTrailingSlash } from "@utils/stripTrainlingSlash";
import Head from "@components/Head";
import { MetaData } from "api/client";



const pageComponents = {
  home: ["NewsHero", "CallOut", "EventsTimeline", "Newsletter"],
  darkHome: ["NewsHero", "EventsTimeline", "CallOut", "Newsletter"],
  bitter: [
    "Hero",
    "BitterBlockConsummation",
    "TimelineYears",
    "AwardsTimeline",
    "BitterBlockManufacturing",
    "BitterBlockOnlyGenuine",
    "BitterBlockAboutLogo",
    "Newsletter",
  ],
  mixology: ["RecipeList"],
  news: ["NewsList", "Newsletter"],
  distributors: ["Distributors"],
  "web-shop": ["Hero", "Products", "Newsletter"],
  contact: ["Contact"],
  events: ["EventsList", "Newsletter"],
  products: ["Hero", "ProductList"],
  original: ["Originality"],
};

const pageComponentsProps = {
  home: {
    CallOut: {
      imageSize: {
        height: 480,
        width: 380,
      },
      hasAction: true,
      action: "/bitter",
      actionText: "UPOZNAJ GORKI LIST",
    },
  },
  darkHome: {
    CallOut: {
      imageSize: {
        height: 480,
        width: 380,
      },
      hasAction: true,
      action: "/bitter",
      actionText: "UPOZNAJ GORKI LIST",
    },
  },
  bitter: {
    BitterBlockConsummation: {
      hasAction: true,
      action: "mixology",
      isBitter: true,
    },
    BitterBlockManufacturing: {},
    BitterBlockAboutLogo: {
      imageSize: {
        height: 500,
        width: 180,
      },
    },
  },
};

const Page = ({ slug, components, headerData, footerData, pages }) => {
  const [light, setLight] = useState(true); //useSelector((state: AppState) => state.light);

  useEffect(() => {
    const time = new Date();
    const hour = time.getHours();
    if (hour >= 19 || hour <= 5) {
      setLight(false);
    }
  }, []);

  const isDark = !light && slug === "home";

  const theme = useTheme<Theme>();
  const { lang } = useTranslation();
  const _slug = slug === "home" && !light ? "darkHome" : slug;

  const dynamicComponents = pageComponents[_slug].map((component) => {
    const c = require(`../../src/components/${component}`).default;
    const data =
      components.find((c) => c.hasOwnProperty(component))[component] || null;

    const props =
      (pageComponentsProps[_slug] && pageComponentsProps[_slug][component]) ||
      {};

    return {
      Component: c,
      data,
      props,
    };
  });

  const metadata = pages.filter((page) => page.slug === slug)[0]
    .meta_data as MetaData;
  const canonicalDomain = process.env.NEXT_PUBLIC_CANONICAL_URL;

  const { asPath } = useRouter();

  return (
    <>
      <div
        css={css`
          background: ${light
            ? theme.palette.background
            : slug === "home"
            ? theme.palette.darkBackground
            : theme.palette.background};
          color: ${theme.palette.text.primary};
          width: 100%;
          transition-duration: 0.2s;
          transition-property: background-color, color;
        `}
      >
        <Head url={`${canonicalDomain}${asPath}`} {...metadata} />
        <LocalizedLayout
          lang={lang}
          headerData={headerData}
          footerData={footerData}
          isDark={isDark}
        >
          {dynamicComponents.map(({ Component, data, props }, id) => {
            return (
              <Component
                key={id}
                data={data}
                lang={lang}
                {...props}
                isDark={isDark}
              />
            );
          })}
        </LocalizedLayout>
      </div>
      <div
        style={{
          position: "absolute",
          top: "8px",
          right: "8px",
        }}
      >
        {/* <DayNightToggle day={light} onClick={handleClick} /> */}
      </div>
    </>
  );
};

export const getStaticProps: GetStaticProps<
  {},
  { lang: string; slug: string }
> = async (context) => {
  try {
    // @ts-ignore context is always defined
    const { lang, slug: _slug } = context.params;

    let slug = _slug || "home";

    const menus = await cacheAsyncFn(api.getMenus, lang, "header");
    const footer = await cacheAsyncFn(api.getMenus, lang, "footer");
    const socials = await cacheAsyncFn(api.getMenuSocials, lang);
    const pages = await cacheAsyncFn(api.getPages, lang);

    const headerData = menus?.menu_items?.map((page) => ({
      href: page.href,
      title: page.title,
      as: stripTrailingSlash(`/${lang}${page.href}`),
    }));

    const footerData = footer?.menu_items?.map((page) => ({
      href: page.href,
      title: page.title,
      as: stripTrailingSlash(`/${lang}${page.href}`),
    }));

    const components = await Promise.all(
      pageComponents[slug].map(async (component) => {
        const c = require(`../../src/components/${component}`).default;

        const componentData = await c.getData(lang, slug);

        return { [component]: componentData.data };
      })
    );
    return {
      props: {
        lang,
        slug,
        namespaces: await importNamespaces(lang, ["common"] as any),
        headerData,
        footerData: [footerData, socials],
        components,
        pages,
      },
    };
  } catch (e) {
    console.log(e, e.trace);
    return {} as any;
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  // const slugs = ["mixology", "news", "bitter"]
  //   .map((entry) => getI18nPaths(entry))
  //   .reduce((arr, v) => (arr.push(...v), arr), []);

  //API should provide all pages
  const pages = await api.getPages("sr");
  const _slugs = pages.map((page) => page.href?.substring(1) || "home");
  // API Should return all pages that we have
  const paths = [..._slugs, "products", "contact", "events"]
    .map((entry) => getI18nPaths(entry))
    .reduce((arr, v) => (arr.push(...v), arr), []);

  return {
    paths,
    fallback: false,
  };
};

export default withI18n(Page);
